import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Admin login screen — visual redesign per login-sketch.png (2026-10-12).
 *
 * Two steps in the same centered white card:
 *   1. email  → envelope illustration + mail input + "שלח קוד"
 *   2. code   → paper-plane illustration + 6-box segmented input + "התחבר" + "שלח שוב"
 *
 * The segmented input is a VISUAL wrapper only — it collects six digits
 * into a single `code: string` (six characters) and feeds it to the
 * existing `verifyCode(email, code)` call. No auth logic was changed.
 */
import React, { useRef, useState } from 'react';
import { Alert, Button, Form, Input, Typography } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { ApiError } from '../api/client';
import { useAuth } from '../AuthContext';
import loginBg from '../assets/login-bg.png';
import iconEmail from '../assets/login-icon-email.png';
import iconSent from '../assets/login-icon-sent.png';
import iconLogo from '../assets/login-icon-logo.png';
const { Title, Text } = Typography;
const OTP_LENGTH = 6;
const PURPLE = '#8B2FD6';
const PURPLE_DEEP = '#6B21A8';
const INK = '#22174B';
const INK_MUTED = '#6D6386';
const LAVENDER_BG = '#EFE9FA';
export default function LoginScreen() {
    const { requestCode, verifyCode } = useAuth();
    const [step, setStep] = useState('email');
    const [email, setEmail] = useState('');
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const onRequest = async (v) => {
        setError(null);
        setBusy(true);
        try {
            await requestCode(v.email.trim());
            setEmail(v.email.trim());
            setStep('code');
        }
        catch (err) {
            setError(err.message || 'שגיאה');
        }
        finally {
            setBusy(false);
        }
    };
    const onVerify = async (code) => {
        setError(null);
        setBusy(true);
        try {
            await verifyCode(email, code.trim());
        }
        catch (err) {
            setError(err instanceof ApiError && err.status === 403
                ? 'המשתמש אינו admin — התחברות נדחתה'
                : err.message || 'שגיאה');
        }
        finally {
            setBusy(false);
        }
    };
    const onResend = async () => {
        if (!email)
            return;
        setError(null);
        setBusy(true);
        try {
            await requestCode(email);
        }
        catch (err) {
            setError(err.message || 'שגיאה');
        }
        finally {
            setBusy(false);
        }
    };
    return (_jsx("div", { style: {
            minHeight: '100vh',
            padding: '24px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // Full-screen bg: solid lavender base + the landscape anchored to
            // the bottom at 100% width, auto height. Its top fades into the
            // same #EFE9FA, so no overlay gradient needed.
            background: `${LAVENDER_BG} url(${loginBg}) center bottom / 100% auto no-repeat`,
        }, children: _jsxs("div", { style: {
                width: '100%',
                maxWidth: 420,
                background: '#ffffff',
                borderRadius: 20,
                boxShadow: '0 10px 40px rgba(107, 33, 168, 0.14), 0 2px 8px rgba(34, 23, 75, 0.06)',
                padding: '32px 28px',
                direction: 'rtl',
            }, children: [_jsx(CardHeader, {}), step === 'email' ? (_jsx(EmailStep, { busy: busy, error: error, onSubmit: onRequest })) : (_jsx(CodeStep, { busy: busy, error: error, email: email, onSubmit: onVerify, onResend: onResend, onBack: () => { setStep('email'); setError(null); } }))] }) }));
}
/* ---------------------------- header ----------------------------------- */
function CardHeader() {
    return (_jsxs("div", { style: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 8,
            marginBottom: 10,
        }, children: [_jsx("img", { src: iconLogo, alt: "", "aria-hidden": true, width: 56, height: 56, style: { display: 'block' } }), _jsx(Title, { level: 4, style: { margin: 0, color: INK, fontWeight: 700, lineHeight: 1.1, fontSize: 32 }, children: "\u05DE\u05EA\u05DE\u05D8\u05D9\u05E7\u05DC\u05D9 \u00B7 \u05E4\u05D0\u05E0\u05DC \u05E0\u05D9\u05D4\u05D5\u05DC" }), _jsx(Text, { style: { fontSize: 18, color: INK_MUTED }, children: "\u05E0\u05D9\u05D4\u05D5\u05DC \u05E4\u05E9\u05D5\u05D8 \u05D9\u05D5\u05EA\u05E8 \u05DC\u05DC\u05DE\u05D9\u05D3\u05D4 \u05DE\u05EA\u05E7\u05D3\u05DE\u05EA \u05D9\u05D5\u05EA\u05E8" })] }));
}
/* ---------------------------- email step ------------------------------- */
function EmailStep({ busy, error, onSubmit }) {
    return (_jsxs(_Fragment, { children: [_jsx("div", { style: { textAlign: 'center', margin: '12px 0 8px' }, children: _jsx("img", { src: iconEmail, alt: "", "aria-hidden": true, width: 200, style: { display: 'inline-block', height: 'auto' } }) }), error && (_jsx(Alert, { type: "error", message: error, showIcon: true, style: { marginBottom: 12, borderRadius: 12 } })), _jsxs(Form, { layout: "vertical", onFinish: onSubmit, requiredMark: false, children: [_jsx(Form.Item, { name: "email", label: _jsx("span", { style: { color: INK, fontWeight: 500 }, children: "\u05DE\u05D9\u05D9\u05DC" }), rules: [
                            { required: true, message: 'מייל נדרש' },
                            { type: 'email', message: 'כתובת מייל לא תקינה' },
                        ], children: _jsx(Input, { autoFocus: true, type: "email", placeholder: "your@email.co.il", suffix: _jsx(MailOutlined, { style: { color: INK_MUTED } }), style: { height: 48, borderRadius: 12 } }) }), _jsx(Button, { type: "primary", htmlType: "submit", loading: busy, block: true, style: { height: 52, borderRadius: 999, fontSize: 16, fontWeight: 600, marginTop: 4 }, children: "\u05E9\u05DC\u05D7 \u05E7\u05D5\u05D3" })] })] }));
}
/* ---------------------------- code step -------------------------------- */
function CodeStep({ busy, error, email, onSubmit, onResend, onBack }) {
    const [digits, setDigits] = useState(() => Array(OTP_LENGTH).fill(''));
    const code = digits.join('');
    const canSubmit = code.length === OTP_LENGTH && digits.every((d) => d !== '');
    const submit = (e) => {
        e?.preventDefault();
        if (!canSubmit)
            return;
        onSubmit(code);
    };
    return (_jsxs(_Fragment, { children: [_jsx("div", { style: { textAlign: 'center', margin: '12px 0 4px' }, children: _jsx("img", { src: iconSent, alt: "", "aria-hidden": true, width: 200, style: { display: 'inline-block', height: 'auto' } }) }), _jsxs("div", { style: { textAlign: 'center', marginBottom: 16 }, children: [_jsx(Title, { level: 5, style: { margin: 0, color: INK, fontWeight: 700 }, children: "\u05E7\u05D5\u05D3 \u05E0\u05E9\u05DC\u05D7 \u05DC\u05DE\u05D9\u05D9\u05DC \u05E9\u05DC\u05DA" }), _jsxs(Text, { style: { fontSize: 13, color: INK_MUTED, display: 'block', marginTop: 4 }, children: ["\u05E9\u05DC\u05D7\u05E0\u05D5 \u05E7\u05D5\u05D3 \u05D1\u05DF 6 \u05E1\u05E4\u05E8\u05D5\u05EA \u05DC-", _jsx(Text, { style: { color: PURPLE_DEEP, fontWeight: 500 }, children: email })] })] }), error && (_jsx(Alert, { type: "error", message: error, showIcon: true, style: { marginBottom: 12, borderRadius: 12 } })), _jsxs("form", { onSubmit: submit, children: [_jsx("div", { style: { display: 'flex', justifyContent: 'center', marginBottom: 20 }, children: _jsx(OtpInput, { digits: digits, setDigits: setDigits, disabled: busy, onComplete: onSubmit }) }), _jsx(Button, { type: "primary", htmlType: "submit", loading: busy, disabled: !canSubmit, block: true, style: { height: 52, borderRadius: 999, fontSize: 16, fontWeight: 600 }, children: "\u05D4\u05EA\u05D7\u05D1\u05E8" })] }), _jsxs("div", { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }, children: [_jsx(Button, { type: "link", onClick: onResend, disabled: busy, style: { padding: 0, color: PURPLE_DEEP }, children: "\u05E9\u05DC\u05D7 \u05E9\u05D5\u05D1" }), _jsx(Button, { type: "link", onClick: onBack, disabled: busy, style: { padding: 0, color: INK_MUTED }, children: "\u05D7\u05D6\u05E8\u05D4" })] })] }));
}
/* --- Segmented 6-digit input ------------------------------------------
 * Pure visual wrapper. Produces a single six-char string, feeds the
 * existing verify call. Keyboard rules:
 *   - typing a digit auto-advances to the next box
 *   - Backspace on an empty box focuses the previous box
 *   - pasting any string: strip non-digits, fill up to six boxes, focus
 *     the last filled one, and auto-submit if six digits landed
 *   - ArrowLeft / ArrowRight navigate (left=i-1, right=i+1 — the row is
 *     laid out LTR because digits read LTR even in an RTL card)
 */
function OtpInput({ digits, setDigits, disabled, onComplete }) {
    const refs = useRef([]);
    const setDigit = (i, v) => {
        const next = digits.slice();
        next[i] = v;
        setDigits(next);
        return next;
    };
    const focusAt = (i) => {
        const el = refs.current[Math.max(0, Math.min(OTP_LENGTH - 1, i))];
        if (el) {
            el.focus();
            el.select();
        }
    };
    const handleChange = (i, raw) => {
        const d = raw.replace(/\D/g, '');
        if (!d) {
            setDigit(i, '');
            return;
        }
        if (d.length === 1) {
            setDigit(i, d);
            if (i < OTP_LENGTH - 1)
                focusAt(i + 1);
            return;
        }
        // Multi-char (paste directly into a box or fast typing): distribute.
        const next = digits.slice();
        for (let k = 0; k < d.length && i + k < OTP_LENGTH; k++)
            next[i + k] = d[k];
        setDigits(next);
        const landed = Math.min(i + d.length, OTP_LENGTH) - 1;
        focusAt(landed);
        if (next.every((x) => x !== '') && onComplete)
            onComplete(next.join(''));
    };
    const handleKeyDown = (i, e) => {
        if (e.key === 'Backspace') {
            if (digits[i]) {
                setDigit(i, '');
            }
            else if (i > 0) {
                e.preventDefault();
                setDigit(i - 1, '');
                focusAt(i - 1);
            }
            return;
        }
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            focusAt(i - 1);
            return;
        }
        if (e.key === 'ArrowRight') {
            e.preventDefault();
            focusAt(i + 1);
            return;
        }
    };
    const handlePaste = (i, e) => {
        const text = e.clipboardData.getData('text') || '';
        const d = text.replace(/\D/g, '').slice(0, OTP_LENGTH - i);
        if (!d)
            return;
        e.preventDefault();
        const next = digits.slice();
        for (let k = 0; k < d.length; k++)
            next[i + k] = d[k];
        setDigits(next);
        focusAt(Math.min(i + d.length, OTP_LENGTH - 1));
        if (next.every((x) => x !== '') && onComplete)
            onComplete(next.join(''));
    };
    return (_jsx("div", { className: "login-otp", children: digits.map((d, i) => (_jsx("input", { ref: (el) => { refs.current[i] = el; }, type: "text", inputMode: "numeric", pattern: "[0-9]*", autoComplete: i === 0 ? 'one-time-code' : 'off', autoFocus: i === 0, disabled: disabled, maxLength: 6, value: d, onChange: (e) => handleChange(i, e.target.value), onKeyDown: (e) => handleKeyDown(i, e), onPaste: (e) => handlePaste(i, e), onFocus: (e) => e.currentTarget.select() }, i))) }));
}
